/**
 * Camera failures on a store tablet, reported and explained.
 *
 * On 2026-09-25 a store tablet lost camera access partway through a receiving
 * count and every remaining item was typed. Nothing about the failure left the
 * tablet, so afterwards there was no way to tell a revoked permission from a
 * camera another app had grabbed. This module fixes both halves of that:
 *
 *   1. `reportCameraFailure()` posts one event to the api's telemetry collector
 *      so the failure is visible to us after the fact.
 *   2. `cameraFailureMessage()` turns the browser's error name into something a
 *      store employee can act on, instead of `e.message`.
 *
 * Transport. The app already loads the shared browser-telemetry snippet
 * (`index.html`, `%VITE_TELEMETRY_SRC%`, `data-site="sweetspot"`), but that file
 * exposes only `window.ampTelemetry = { flush, site, api }` — there is no way to
 * hand it an app event — so this posts to the collector directly. The wire shape
 * and the send path mirror the snippet's own: `sendBeacon` with a `text/plain`
 * body first, which is a CORS-simple request (no preflight) and carries the
 * `.ampersandbrands.com` session cookie, falling back to `fetch` with
 * `keepalive`. The collector resolves who the user is from that cookie
 * server-side and stamps the request's user agent, which is why neither is in
 * the payload — the client never sends identity.
 *
 * Nothing here is awaited by the UI, and nothing here can throw into it. A
 * collector outage must cost the store nothing; the scan already failed.
 */

import { reportTelemetryEvent } from './reportTelemetryEvent';

const KEEP_COUNTING = 'You can keep counting by typing the item number.';

/**
 * What the store sees, per error name. These are deliberately about the next
 * action, not the cause: `NotAllowedError` means the permission is off in Chrome
 * site settings or the Scalefusion policy, neither of which store staff can
 * change themselves, so the message sends them to IT rather than telling them to
 * try again.
 */
const GUIDANCE: Record<string, string> = {
  NotAllowedError:
    'Camera access is blocked for Sweet Spot. Ask IT to re-enable camera access on this tablet.',
  NotReadableError:
    'The camera is busy or not responding. Close other apps using the camera, or restart the tablet.',
  AbortError: 'The camera stopped unexpectedly. Tap Scan to try again.',
  NotFoundError: 'No camera was found on this device.',
  DecoderLoadFailed: 'The barcode reader did not load. Refresh the page to try again.',
  NativeScanFailed:
    'The scanner could not use the camera. Ask IT to verify camera access for The Sweet Spot.',
};

const GENERIC = 'The camera could not be started.';

/** The on-screen message for a camera failure. Always offers the manual way out. */
export function cameraFailureMessage(errorName: string): string {
  return `${GUIDANCE[errorName] ?? GENERIC} ${KEEP_COUNTING}`;
}

export type CameraFailure = {
  /** Browser error name, `DecoderLoadFailed`, or `NativeScanFailed`. */
  errorName: string;
  errorMessage: string;
  /** Store the scan belonged to, when the page knows it. */
  storeNo?: number | string | null;
  /** Enrolled tablet's device id, or null on a personal sign-in. */
  deviceId?: number | null;
};

type CameraPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Whether the browser thinks it has camera permission. This is the field that
 * separates "IT revoked it" from "the hardware is busy", so it is worth asking
 * for — but the Permissions API does not exist in every Android WebView and
 * throws for the `camera` name in some that do have it. Both cases are
 * `unknown`, never a lost report.
 */
async function cameraPermission(): Promise<CameraPermission> {
  try {
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return status.state as CameraPermission;
  } catch {
    return 'unknown';
  }
}

/**
 * Report one camera failure. Fire and forget: returns immediately, never throws,
 * never retries.
 */
export function reportCameraFailure(failure: CameraFailure): void {
  void (async () => {
    try {
      const perm = await cameraPermission();
      const msg =
        `${failure.errorName}: ${failure.errorMessage}` +
        ` | perm=${perm}` +
        ` | store=${failure.storeNo ?? 'unknown'}` +
        ` | device=${failure.deviceId ?? 'none'}`;
      reportTelemetryEvent({
        event: 'error',
        kind: 'camera',
        type: failure.errorName,
        message: msg,
      });
    } catch {
      /* ignore */
    }
  })();
}
